/**
 * 환경변수는 두 가지 경로로 주입됨:
 * 1. htmlEnvInjectionPlugin → window.* (런타임, VITE_ prefix 제거)
 * 2. import.meta.env.VITE_* (빌드타임)
 *
 * window.* 를 우선 참조하고, fallback으로 import.meta.env 사용
 */

declare global {
  interface Window {
    ENV?: string;
    PROJECT?: string;
    API_ENDPOINT?: string;
    HOST?: string;
    WEB_HOST?: string;
    ADMIN_HOST?: string;
    PRIVACY_URL?: string;
    TERMS_URL?: string;
    AWS_REGION?: string;
    MEDIA_BUCKET?: string;
    MEDIA_PREFIX?: string;
    SHARE_ENABLED?: string;
  }
}

export const ENV = window.ENV || import.meta.env.VITE_ENV || 'LOCAL';
export const PROJECT = window.PROJECT || import.meta.env.VITE_PROJECT || '';
/**
 * muzly 백엔드의 단일 base — `muz-d1` 스테이지 하나 아래 `/oauth/*`와 `/profile/*`이 같이 산다.
 * 비어 있으면 서버를 쓰는 기능(Device Identity·프로필 동기)이 전부 꺼진다.
 */
export const API_ENDPOINT =
  window.API_ENDPOINT || import.meta.env.VITE_API_ENDPOINT || '';
export const HOST = window.HOST || import.meta.env.VITE_HOST || '';
export const WEB_HOST = window.WEB_HOST || import.meta.env.VITE_WEB_HOST || '';
export const ADMIN_HOST =
  window.ADMIN_HOST || import.meta.env.VITE_ADMIN_HOST || '';
/** 개인정보처리방침 URL(이 앱의 `/policy/privacy`) — 비면 MY 가 앱 안 문서(`/more/docs/privacy`)로 간다. */
export const PRIVACY_URL =
  window.PRIVACY_URL || import.meta.env.VITE_PRIVACY_URL || '';
/** 서비스 이용약관 URL(이 앱의 `/policy/terms`) — 비면 MY 가 앱 안 문서(`/more/docs/terms`)로 간다. */
export const TERMS_URL =
  window.TERMS_URL || import.meta.env.VITE_TERMS_URL || '';
export const AWS_REGION =
  window.AWS_REGION || import.meta.env.VITE_AWS_REGION || 'ap-northeast-2';
/**
 * 진단 녹음을 올리는 S3 버킷. **비면 업로드 경로 전체가 꺼진다** — 파일을 읽지도, 서명하지도
 * 않는다. 키 프리픽스는 버킷 정책·IAM 조건이 `medias/<stage>/<identityId>/` 로 강제한다.
 */
export const MEDIA_BUCKET =
  window.MEDIA_BUCKET || import.meta.env.VITE_MEDIA_BUCKET || '';
/**
 * 업로드 키의 프리픽스. IAM 이 `<prefix>/${cognito-identity.amazonaws.com:sub}/*` 로
 * 쓰기를 제한하고 버킷 정책이 같은 자리에 공개 읽기를 연다 — 즉 이 값은 취향이 아니라
 * 인프라와 맞아야 하는 값이다.
 *
 * **기본값을 두지 않는다.** 스테이지마다 다른데 하나를 기본으로 박으면, 다른 스테이지에서
 * 값을 빠뜨렸을 때 조용히 틀린 프리픽스로 올려 403 을 받는다. 비면 업로드가 꺼진다 —
 * 꺼진 것은 눈에 띄지만 틀린 프리픽스는 안 띈다.
 */
export const MEDIA_PREFIX =
  window.MEDIA_PREFIX || import.meta.env.VITE_MEDIA_PREFIX || '';
/**
 * 결과 상세의 공유 버튼. **`'true'` 일 때만 켜진다** — 비어 있거나 다른 값이면 꺼진다(기본 꺼짐).
 * 공유 링크가 여는 공개 상세 응답이 비소유자에게 녹음 주소를 내주면 안 된다 — 그 스테이지의
 * 백엔드가 그걸 빼는 것(muzly-io/muzly-backend-api#5)을 확인한 env 파일에서만 켠다. dev 는
 * 2026-10-08 에 확인하고 켰다. 운영은 같은 날 배포됐다고 전달받아 켰다(운영 응답 대조는 미검증). 수신 라우트 `/share/:id` 는 이 값과
 * 무관하게 열려 있다.
 */
export const SHARE_ENABLED =
  (window.SHARE_ENABLED || import.meta.env.VITE_SHARE_ENABLED) === 'true';
export const IS_DEV = ENV === 'LOCAL' || ENV === 'DEV';
export const IS_PROD = ENV === 'PROD';
